import type { MatchTip, MatchTipWithProfile } from "../types/index.ts";
import type { Profile } from "../supaBaseClient.ts";

const SUPABASE_URL = "https://fvuwfonfrllbjqvywula.supabase.co";

// Same hybrid REST pattern as TournamentService/TipRoundService: raw fetch +
// anon key for public reads, the user's access_token for authenticated
// writes (RLS-protected via Migration 042's own/deadline/pending policies).

const anonHeaders = (): Record<string, string> => ({
  apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
  "Content-Type": "application/json",
});

const PROJECT_REF = "fvuwfonfrllbjqvywula";
const SESSION_STORAGE_KEY = `sb-${PROJECT_REF}-auth-token`;

const getAccessTokenFromStorage = (): string | null => {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.access_token ?? parsed?.currentSession?.access_token ?? null;
  } catch {
    return null;
  }
};

const authedHeaders = (): Record<string, string> => {
  const token = getAccessTokenFromStorage() ?? import.meta.env.VITE_SUPABASE_ANON_KEY;
  return {
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
};

/**
 * Persistence for the Liga "Klassisch" single-match result tip (tips_match,
 * Migration 042). One row per (user, tip_round, fixture) — unlike the
 * table tip (tips_v2, one JSON blob per user/round/league), each match is
 * its own row so per-fixture deadlines (RESTRICTIVE RLS against
 * matchday_schedule.first_kickoff_utc) can be enforced individually.
 *
 * Scoring itself is NOT done here — callers combine these rows with
 * LeagueFixtureService fixtures (for the locked odds + result) and
 * scoreClassicMatch from tournamentScoring.ts.
 */
export class MatchTipService {
  private static instance: MatchTipService;
  private constructor() {}

  public static getInstance(): MatchTipService {
    if (!MatchTipService.instance) {
      MatchTipService.instance = new MatchTipService();
    }
    return MatchTipService.instance;
  }

  /** The current user's own match tips for a round (all fixtures/matchdays). */
  async getUserTips(userId: string, tipRoundId: number): Promise<MatchTip[]> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tips_match?user_id=eq.${userId}&tip_round_id=eq.${tipRoundId}&select=*`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!res.ok) return [];
      return (await res.json()) as MatchTip[];
    } catch (e) {
      console.error("MatchTipService.getUserTips failed:", e);
      return [];
    }
  }

  /**
   * Upsert one match tip. Rejected by RLS (silently returns null, not a
   * throw) once the fixture's matchday has kicked off, or while the user is
   * a 'pending' member of an 'approval'-mode round — same shape as
   * LeagueService.saveUserTipsForLeague's error handling.
   */
  async saveTip(
    userId: string,
    tipRoundId: number,
    leagueId: number,
    fixtureId: number,
    homeGoals: number,
    awayGoals: number,
  ): Promise<MatchTip | null> {
    const payload = {
      user_id: userId,
      tip_round_id: tipRoundId,
      league_id: leagueId,
      fixture_id: fixtureId,
      home_goals: homeGoals,
      away_goals: awayGoals,
      updated_at: new Date().toISOString(),
    };
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tips_match?on_conflict=user_id,tip_round_id,fixture_id`,
        {
          method: "POST",
          headers: {
            ...authedHeaders(),
            Prefer: "resolution=merge-duplicates,return=representation",
          },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        console.error(
          "MatchTipService.saveTip failed:",
          res.status,
          await res.text(),
        );
        return null;
      }
      const rows = (await res.json()) as MatchTip[];
      return rows[0] ?? null;
    } catch (e) {
      console.error("MatchTipService.saveTip error:", e);
      return null;
    }
  }

  /** All users' match tips for a round + league (for the classic ranking). */
  async getAllTipsForRound(
    tipRoundId: number,
    leagueId: number,
  ): Promise<MatchTipWithProfile[]> {
    try {
      const tipsRes = await fetch(
        `${SUPABASE_URL}/rest/v1/tips_match?tip_round_id=eq.${tipRoundId}&league_id=eq.${leagueId}&select=*`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!tipsRes.ok) return [];
      const tips = (await tipsRes.json()) as MatchTip[];
      if (tips.length === 0) return [];

      const userIds = [...new Set(tips.map((t) => t.user_id))];
      const profilesRes = await fetch(
        `${SUPABASE_URL}/rest/v1/profiles?select=user_id,display_name&user_id=in.(${userIds
          .map((id) => `"${id}"`)
          .join(",")})`,
        { method: "GET", headers: anonHeaders() },
      );
      const profiles = profilesRes.ok ? ((await profilesRes.json()) as Profile[]) : [];
      const profileByUser = new Map(profiles.map((p) => [p.user_id, p]));

      return tips.map((tip) => ({ ...tip, profile: profileByUser.get(tip.user_id) }));
    } catch (e) {
      console.error("MatchTipService.getAllTipsForRound failed:", e);
      return [];
    }
  }

  /**
   * Whether the given user has an "Add-on" table tip for this round/league
   * — used to decide participation in the Klassik mode's table tab (derived
   * from tip existence, no separate opt-in flag; see product decision).
   * Callers typically already have all tips_v2 rows loaded and can check
   * existence directly — this helper exists for the one-off case.
   */
  async hasTableTip(userId: string, tipRoundId: number, leagueId: number): Promise<boolean> {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/tips_v2?user_id=eq.${userId}&tip_round_id=eq.${tipRoundId}&league_id=eq.${leagueId}&select=id&limit=1`,
        { method: "GET", headers: anonHeaders() },
      );
      if (!res.ok) return false;
      const rows = (await res.json()) as Array<{ id: number }>;
      return rows.length > 0;
    } catch (e) {
      console.error("MatchTipService.hasTableTip failed:", e);
      return false;
    }
  }
}

export const matchTipService = MatchTipService.getInstance();
